import type { OnBoardingPageContent, TenantContent } from '@/types/tenant.types';

/**
 * Tenant image URLs — mirrors Flutter NetworkImageConstant.setContent().
 *
 * A configured image is only used when its type is set; otherwise the tenant's
 * default asset on the public image host is used
 * (https://io-public.spire2grow.com/<workspaceId>/<file>).
 */
const IMAGE_HOST_URL = 'https://io-public.spire2grow.com/';

function resolveImage(
  content: TenantContent | null,
  configured: { url?: string; type?: string | null } | undefined,
  defaultFile: string,
): string | undefined {
  if (configured?.type && configured.url) return configured.url;
  if (!content?.workspaceId) return undefined;
  return `${IMAGE_HOST_URL}${content.workspaceId}/${defaultFile}`;
}

/** Colored logo — NetworkImageConstant.logoImage */
export function getLogoImageUrl(content: TenantContent | null): string | undefined {
  return resolveImage(content, content?.contentSettings?.general?.logoProperties, 'appLogoColored.png');
}

/** White logo — NetworkImageConstant.onBoardingLogo (general.logoPropertiesWhite) */
export function getWhiteLogoUrl(content: TenantContent | null): string | undefined {
  return resolveImage(content, content?.contentSettings?.general?.logoPropertiesWhite, 'appLogoWhite.png');
}

/**
 * Logo box size — NetworkImageConstant.logoImageHeight / logoImageWidth: logoProperties.height
 * (default 8) as a percentage of the viewport height and .width (default 16) of its width,
 * like Flutter's responsive_sizer `.h` / `.w`. A 0 size counts as unset (Flutter LogoProperties.toEntity).
 */
export function getLogoImageSize(content: TenantContent | null): { height: string; width: string } {
  const logo = content?.contentSettings?.general?.logoProperties;
  return { height: `${logo?.height || 8}vh`, width: `${logo?.width || 16}vw` };
}

/**
 * Logo on the auth forms (sign in, sign up, verify email, forgot password, MFA) — shown only
 * when general.authenticationLogoConfig.showLogo is true. logoType 'white' picks the white logo,
 * anything else the colored one. Mirrors GenericHelpers.getAuthenticationPageLogoURL().
 */
export function getAuthLogoUrl(content: TenantContent | null): string | undefined {
  const config = content?.contentSettings?.general?.authenticationLogoConfig;
  if (config?.showLogo !== true) return undefined;
  return config.logoType === 'white' ? getWhiteLogoUrl(content) : getLogoImageUrl(content);
}

/** Branding banner — NetworkImageConstant.homeHeaderImage (contentSettings.header.background) */
export function getBrandingBannerUrl(content: TenantContent | null): string | undefined {
  const image = content?.contentSettings?.header?.background;
  return resolveImage(content, { url: image?.imageUrl, type: image?.imageType }, 'banner_image.jpg');
}

/**
 * Home page banner image — what HomeBanner shows: the desktop/mobile banner preference's
 * imageUrl, else the branding banner.
 */
export function getHomeBannerImageUrl(
  content: TenantContent | null,
  preferences: Record<string, unknown> | null,
  isMobile = false,
): string | undefined {
  const banner = preferences?.[isMobile ? 'mobileBanner' : 'desktopBanner'] as { imageUrl?: string } | undefined;
  return banner?.imageUrl || getBrandingBannerUrl(content);
}

/** Job details hero card background — NetworkImageConstant.jobDetailsHeaderImage (contentSettings.jobDetails.jobDetailsPageImage) */
export function getJobDetailsHeaderImageUrl(content: TenantContent | null): string | undefined {
  const image = content?.contentSettings?.jobDetails?.jobDetailsPageImage;
  return resolveImage(content, { url: image?.imageUrl, type: image?.imageType }, 'jobDescriptionBanner.jpg');
}

// ── Onboarding panel (login, sign-up and MFA pages) ──────────────

function getOnBoardingPage(content: TenantContent | null): OnBoardingPageContent | undefined {
  return content?.contentSettings?.onBoardingPage;
}

/** Onboarding image — NetworkImageConstant.onBoardingImage (onBoardingPage.onBoardingPageImage) */
export function getOnBoardingImageUrl(content: TenantContent | null): string | undefined {
  const image = getOnBoardingPage(content)?.onBoardingPageImage;
  return resolveImage(content, { url: image?.imageUrl, type: image?.imageType }, 'onBoarding.png');
}

/**
 * Sign-up page image — NetworkImageConstant.onBoardingSignUpImage (onBoardingPage.onBoardingSignUpPageImage).
 * It has no default file: when it isn't configured, WebOnboardingPage shows the onboarding image.
 */
export function getOnBoardingSignUpImageUrl(content: TenantContent | null): string | undefined {
  const image = getOnBoardingPage(content)?.onBoardingSignUpPageImage;
  if (image?.imageType && image.imageUrl) return image.imageUrl;
  return getOnBoardingImageUrl(content);
}

/** Flutter Background.toEntity(): a 0 size counts as unset */
function imageSize(value: number | undefined): number | undefined {
  return value ? value : undefined;
}

/**
 * The onboarding image box, as WebOnboardingPage sizes it: onBoardingPageImage.imageWidth
 * (default 40) as vw and imageHeight (default 50) as vh — NetworkImageConstant.onBoardingImageWidth
 * / Height. The sign-up page (`signUp`) passes the sign-up image's own sizes, which fall back to
 * those; a sign-up imageHeight of -1 means no fixed height (enableOnboardingImageHeight: false).
 */
export function getOnBoardingImageSize(
  content: TenantContent | null,
  signUp = false,
): { width: string; height?: string } {
  const page = getOnBoardingPage(content);
  const width = imageSize(page?.onBoardingPageImage?.imageWidth) ?? 40;
  const height = imageSize(page?.onBoardingPageImage?.imageHeight) ?? 50;
  if (!signUp) return { width: `${width}vw`, height: `${height}vh` };

  const signUpWidth = imageSize(page?.onBoardingSignUpPageImage?.imageWidth) ?? width;
  const signUpHeight = imageSize(page?.onBoardingSignUpPageImage?.imageHeight);
  if (signUpHeight === -1) return { width: `${signUpWidth}vw` };
  return { width: `${signUpWidth}vw`, height: `${signUpHeight ?? height}vh` };
}

/** Flutter BoxFit (GenericHelpers.getBoxFit) as CSS background-size. scaleDown has no CSS equivalent, so it is shown as contain. */
const BOX_FIT_BACKGROUND_SIZE: Record<string, string> = {
  contain: 'contain',
  fitHeight: 'auto 100%',
  fitWidth: '100% auto',
  cover: 'cover',
  none: 'auto',
  fill: '100% 100%',
  scaleDown: 'contain',
};

/** Flutter Alignment (GenericHelpers.getAlignment) as CSS background-position */
const ALIGNMENT_BACKGROUND_POSITION: Record<string, string> = {
  topLeft: 'left top',
  topRight: 'right top',
  bottomLeft: 'left bottom',
  bottomRight: 'right bottom',
  center: 'center',
  centerLeft: 'left center',
  centerRight: 'right center',
};

/**
 * How the onboarding image is fitted — onBoardingPage.onBoardingImageFitValue (default contain)
 * and onBoardingImageAlignment (default center), as WebOnboardingPage passes them to the image.
 */
export function getOnBoardingImageLayout(content: TenantContent | null): {
  backgroundSize: string;
  backgroundPosition: string;
} {
  const page = getOnBoardingPage(content);
  return {
    backgroundSize: BOX_FIT_BACKGROUND_SIZE[page?.onBoardingImageFitValue ?? ''] ?? 'contain',
    backgroundPosition: ALIGNMENT_BACKGROUND_POSITION[page?.onBoardingImageAlignment ?? ''] ?? 'center',
  };
}

/** WebOnboardingPage shows the white logo unless onBoardingPage.showLogo is false. */
export function showOnBoardingLogo(content: TenantContent | null): boolean {
  return getOnBoardingPage(content)?.showLogo !== false;
}
